import { z } from 'zod';
export const difficultySchema = z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED']);
export const submissionScopeSchema = z.enum(['ORGANIZATION', 'PLATFORM']);
export const submissionStatusSchema = z.enum(['SUBMITTED', 'APPROVED', 'REJECTED', 'WITHDRAWN']);
/** Afgeleid uit de publicatiewijzers (D-026). */
export const visibilitySchema = z.enum(['PRIVATE', 'ORGANIZATION', 'PUBLIC']);
export const categorySchema = z.object({ id: z.string(), slug: z.string(), name: z.string() });
export const categoriesResponseSchema = z.object({ categories: z.array(categorySchema) });
// --- Invoer -------------------------------------------------------------------
export const optionalText = (max) => z
    .string()
    .trim()
    .max(max, { message: `Gebruik maximaal ${max} tekens.` })
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const textList = (maxItems, maxLength) => z
    .array(z
    .string()
    .trim()
    .max(maxLength, { message: `Gebruik maximaal ${maxLength} tekens per regel.` }))
    .transform((list) => list.filter((v) => v !== ''))
    .pipe(z.array(z.string()).max(maxItems, { message: `Gebruik maximaal ${maxItems} regels.` }))
    .default([]);
/** Onbekend = null, nooit 0 (docs/03). */
export const optionalInt = (min, max, label) => z
    .number({ message: `${label} moet een getal zijn.` })
    .int({ message: `${label} moet een heel getal zijn.` })
    .min(min, { message: `${label} moet minimaal ${min} zijn.` })
    .max(max, { message: `${label} mag maximaal ${max} zijn.` })
    .nullable()
    .optional();
export const exerciseContentSchema = z
    .object({
    title: z
        .string()
        .trim()
        .min(3, { message: 'Geef de oefening een titel van minimaal 3 tekens.' })
        .max(160, { message: 'Gebruik maximaal 160 tekens.' }),
    summary: optionalText(1200),
    objective: optionalText(1000),
    instructions: textList(30, 1000),
    playerSetup: optionalText(1000),
    minPlayers: optionalInt(1, 60, 'Minimum aantal spelers'),
    maxPlayers: optionalInt(1, 60, 'Maximum aantal spelers'),
    durationMinutes: optionalInt(1, 180, 'Duur'),
    difficulty: difficultySchema.nullable().optional(),
    equipment: textList(30, 200),
    coachingPoints: textList(30, 500),
    variations: textList(30, 1000),
    primaryCategoryId: z.string().min(1, { message: 'Kies een hoofdcategorie.' }),
    categoryIds: z.array(z.string().min(1)).max(9).default([]),
    tags: z
        .array(z.string().trim().min(1).max(40))
        .max(10, { message: 'Gebruik maximaal 10 tags.' })
        .default([]),
})
    .refine((v) => v.minPlayers == null || v.maxPlayers == null || v.minPlayers <= v.maxPlayers, {
    message: 'Het minimum aantal spelers mag niet groter zijn dan het maximum.',
    path: ['maxPlayers'],
});
export const submitExerciseRequestSchema = z.object({
    scope: submissionScopeSchema,
    /** D-032: verplicht true als er een video aan de oefening hangt. */
    includeVideo: z.boolean().optional(),
});
export const rejectSubmissionRequestSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(5, {
        message: 'Geef een reden van minimaal 5 tekens, zodat de auteur weet wat er beter kan.',
    })
        .max(500, { message: 'Gebruik maximaal 500 tekens.' }),
});
export const catalogScopeSchema = z.enum(['all', 'mine', 'club', 'public']);
/** Querystring van GET /exercises; alles komt als string binnen. */
export const exerciseListQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    scope: catalogScopeSchema.default('all'),
    category: z.string().max(40).optional(),
    difficulty: difficultySchema.optional(),
    players: z.coerce.number().int().min(1).max(60).optional(),
    maxDuration: z.coerce.number().int().min(1).max(180).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(24),
});
// --- Uitvoer ------------------------------------------------------------------
export const exerciseVersionSchema = z.object({
    id: z.string(),
    versionNumber: z.number().int(),
    title: z.string(),
    summary: z.string().nullable(),
    objective: z.string().nullable(),
    instructions: z.array(z.string()),
    playerSetup: z.string().nullable(),
    minPlayers: z.number().int().nullable(),
    maxPlayers: z.number().int().nullable(),
    durationMinutes: z.number().int().nullable(),
    difficulty: difficultySchema.nullable(),
    equipment: z.array(z.string()),
    coachingPoints: z.array(z.string()),
    variations: z.array(z.string()),
    primaryCategory: categorySchema.nullable(),
    categories: z.array(categorySchema),
    tags: z.array(z.string()),
    locked: z.boolean(),
    createdAt: z.string(),
});
/** Kaart in de catalogus: de voor deze gebruiker zichtbare versie. */
export const exerciseCardSchema = z.object({
    id: z.string(),
    versionId: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    durationMinutes: z.number().int().nullable(),
    minPlayers: z.number().int().nullable(),
    maxPlayers: z.number().int().nullable(),
    difficulty: difficultySchema.nullable(),
    primaryCategory: categorySchema.nullable(),
    visibility: visibilitySchema,
    isMine: z.boolean(),
    /** Alleen voor eigen oefeningen: stand van de laatste versie. */
    status: z.enum(['DRAFT', 'SUBMITTED', 'PUBLISHED', 'REJECTED']).nullable(),
    updatedAt: z.string(),
});
export const exerciseListResponseSchema = z.object({
    exercises: z.array(exerciseCardSchema),
    nextCursor: z.string().nullable(),
});
export const submissionSummarySchema = z.object({
    id: z.string(),
    scope: submissionScopeSchema,
    status: submissionStatusSchema,
    versionNumber: z.number().int(),
    organizationName: z.string().nullable(),
    reason: z.string().nullable(),
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
});
/** D-032: afspeelbare video bij een oefening; PROCESSING zolang de kopie gemaakt wordt. */
export const exerciseVideoSchema = z.object({ status: z.enum(['PROCESSING', 'READY']) });
export const exerciseDetailSchema = z.object({
    id: z.string(),
    visibility: visibilitySchema,
    isMine: z.boolean(),
    authorName: z.string(),
    organizationName: z.string().nullable(),
    /** De versie die deze gebruiker ziet (voor de auteur: de laatste). */
    version: exerciseVersionSchema,
    archived: z.boolean(),
    /** Video bij de oefening (D-032); afspelen via /api/v1/exercises/:id/video. */
    video: exerciseVideoSchema.nullable(),
    /** Alleen voor de auteur. */
    author: z
        .object({
        draftVersionNumber: z.number().int().nullable(),
        orgPublishedVersionNumber: z.number().int().nullable(),
        publicPublishedVersionNumber: z.number().int().nullable(),
        canSubmitToOrganization: z.boolean(),
        submissions: z.array(submissionSummarySchema),
    })
        .nullable(),
});
export const exerciseDetailResponseSchema = z.object({ exercise: exerciseDetailSchema });
// --- Moderatie ----------------------------------------------------------------
export const moderationItemSchema = z.object({
    id: z.string(),
    scope: submissionScopeSchema,
    exerciseId: z.string(),
    title: z.string(),
    versionNumber: z.number().int(),
    submittedByName: z.string(),
    organizationName: z.string().nullable(),
    /** true als er al een eerder gepubliceerde versie in deze scope is. */
    isUpdate: z.boolean(),
    createdAt: z.string(),
});
export const moderationListResponseSchema = z.object({
    submissions: z.array(moderationItemSchema),
});
export const moderationDetailSchema = moderationItemSchema.extend({
    status: submissionStatusSchema,
    version: exerciseVersionSchema,
    /** Huidige gepubliceerde versie in deze scope, voor de vergelijking. */
    publishedVersion: exerciseVersionSchema.nullable(),
    /** De auteur bevestigde dat de video meegaat; afspelen via de oefening. */
    includesVideo: z.boolean(),
    video: exerciseVideoSchema.nullable(),
});
export const moderationDetailResponseSchema = z.object({ submission: moderationDetailSchema });
//# sourceMappingURL=exercises.js.map